import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Col, Form, Layout, Row, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { SiteBranding } from 'parts';
import { Button } from 'components';
import { PAGES } from 'router/constants';
import { ReactComponent as IconArrowLeft } from 'static/icons/arrow_left.svg';
import { ReactComponent as IconArrowRight } from 'static/icons/arrow_right.svg';
import { ReactComponent as IconSuccess } from 'static/inons_large/success.svg';
import { ReactComponent as IconError } from 'static/inons_large/failure.svg';
import {
  getAvailableDivisions,
  getAvailableSeasons,
  getNDAChecked,
  postTeamRegistration,
} from 'fetcher/team-registration';
import { extractFileObject } from 'components/Upload/utils';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import './style.scss';
import { Option } from '../../components/Select/types';

export const TeamRegistrationContext = createContext({
  loadingNDACheck: false,
  loadingSave: false,
  finalLoading: false,
  divisionsOptions: [] as Option[],
  seasonsOptions: [] as Option[],
});

const STEP_FIELDS = [
  ['logo', 'name', 'zip_code', 'city', 'state', 'division', 'anticipated_season', 'numU13', 'numU15', 'numU17'],
  ['first_name', 'last_name', 'email', 'primary_contact'],
  ['web_site', 'twitter', 'instagram', 'facebook'],
  ['nda'],
];

export const TeamRegistration = () => {
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [isNDAVisited, setIsNDAVisited] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState({
    loadingNDACheck: false,
    loadingSave: false,
    finalLoading: false,
  });
  const [divisionsOptions, setDivisionsOptions] = useState<Option[]>([]);
  const [seasonsOptions, setSeasonsOptions] = useState<Option[]>([]);

  const updateLoadingState = useCallback(
    (key: keyof typeof requestsLoading, value: boolean) => setRequestsLoading((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const checkNDA = useCallback(
    async (isFinalCheck: boolean) => {
      const data = { ...form.getFieldsValue(true) };
      if (!data.application_id) return;

      const fieldKey = isFinalCheck ? 'finalLoading' : 'loadingNDACheck';
      updateLoadingState(fieldKey, true);

      try {
        const { signed } = await getNDAChecked(data.application_id);
        await form.setFieldsValue({ nda: signed || undefined });
        await form.validateFields(STEP_FIELDS[step]);
        setOpenSuccess(true);
      } catch {
        form.setFieldsValue({ nda: undefined });
        form.validateFields(STEP_FIELDS[step]);
      } finally {
        updateLoadingState(fieldKey, false);
      }
    },
    [form, step, updateLoadingState],
  );

  const handleSave = async () => {
    const fields = STEP_FIELDS[step];
    await form.validateFields(fields);

    const data = {
      ...form.getFieldsValue(true),
      secondary_contact: '-',
    };

    data.primary_contact = `+1${data.primary_contact.replace(/\D/g, '')}`;

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'logo') {
        const fileObject = extractFileObject(value);
        if (fileObject) {
          formData.append(key, fileObject);
        }
      } else {
        formData.append(key, value as any);
      }
    });

    updateLoadingState('loadingSave', true);
    try {
      const resp = await postTeamRegistration(formData);
      form.setFieldsValue({
        application_id: resp.application_id,
        signing_url: resp.signing_url,
      });
      setStep((s) => s + 1);
    } finally {
      updateLoadingState('loadingSave', false);
    }
  };

  const prev = () => {
    setIsNDAVisited(false);
    setStep((s) => s - 1);
  };

  const next = async () => {
    try {
      if (step === 2) {
        await handleSave();
      } else if (step === STEP_FIELDS.length - 1) {
        await checkNDA(true);
      } else {
        await form.validateFields(STEP_FIELDS[step]);
        setStep((s) => s + 1);
      }
    } catch (e) {
      //
    }
  };

  const onValuesChange = () => {
    if (!isFormTouched) setIsFormTouched(true);
  };

  const onCloseError = () => {
    form.resetFields();
    setStep(0);
    setOpenSuccess(false);
    setOpenError(false);
    setIsNDAVisited(false);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isNDAVisited) checkNDA(false);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isNDAVisited, checkNDA]);

  useEffect(() => {
    getAvailableDivisions().then((resp) => {
      setDivisionsOptions(resp);
      form.setFieldsValue({ division: resp[0].value });
    });
    getAvailableSeasons().then((resp) => {
      setSeasonsOptions(resp);
      form.setFieldsValue({ anticipated_season: resp[0].value });
    });
  }, []);

  return (
    <div className="page-team-registration">
      <Layout className="page-team-registration__content">
        <Row>
          <Col lg={{ flex: '304px' }} md={{ flex: '100%' }} span={24}>
            <SiteBranding isTouched={isFormTouched} lang="en" />
          </Col>
          {openSuccess && <SuccessMessage />}
          {openError && <ErrorMessage onExit={onCloseError} />}
          {!openSuccess && !openError && (
            <Col className="form-block" lg={{ flex: '1' }} span={24}>
              <Row className="form-block__header" justify="space-between">
                <Col sm={{ span: 24 }} md={{ flex: 'none' }}>
                  <Typography.Title level={3}>League Membership Request</Typography.Title>
                </Col>
                <Col sm={{ span: 24 }} md={{ flex: 'none' }}>
                  <Typography.Text>
                    <b>{step + 1}</b>
                    <span> / 4 Steps</span>
                  </Typography.Text>
                </Col>
              </Row>
              <Row className="form-block__content">
                <Col sm={{ span: 24 }}>
                  <Form form={form} validateTrigger="onBlur" onValuesChange={onValuesChange}>
                    <TeamRegistrationContext.Provider value={{ ...requestsLoading, divisionsOptions, seasonsOptions }}>
                      <TeamRegistrationSteps step={step} onVisitNDA={() => setIsNDAVisited(true)} />
                    </TeamRegistrationContext.Provider>
                  </Form>
                </Col>
              </Row>
              <Row className="form-block__footer">
                <Col span={24}>
                  <div className="action-buttons">
                    {!step && (
                      <Link to={PAGES.HOME}>
                        <Button icon={<IconArrowLeft />} size="large" type="default">
                          Back
                        </Button>
                      </Link>
                    )}
                    {!!step && (
                      <Button
                        icon={<IconArrowLeft />}
                        size="large"
                        type="default"
                        onClick={prev}
                        disabled={
                          requestsLoading.loadingSave || requestsLoading.loadingNDACheck || requestsLoading.finalLoading
                        }
                      />
                    )}
                    {step !== 3 && (
                      <Button
                        icon={<IconArrowRight />}
                        size="large"
                        iconPosition="end"
                        onClick={next}
                        loading={requestsLoading.loadingSave}
                      >
                        Next
                      </Button>
                    )}
                    {step === 3 && (
                      <Button
                        size="large"
                        onClick={next}
                        disabled={requestsLoading.loadingNDACheck || requestsLoading.finalLoading}
                        loading={requestsLoading.loadingNDACheck || requestsLoading.finalLoading}
                      >
                        Submit Request
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Layout>
    </div>
  );
};

const TeamRegistrationSteps = ({ step, onVisitNDA }: { step: number; onVisitNDA: () => void }) => {
  switch (step) {
    case 0:
      return <Step1 />;
    case 1:
      return <Step2 />;
    case 2:
      return <Step3 />;
    case 3:
      return <Step4 onVisitNDA={onVisitNDA} />;
    default:
      return null;
  }
};

const SuccessMessage = () => {
  return (
    <Col flex="1" className="team-registration__message team-registration__message-success">
      <div>
        <IconSuccess className="icon" />
        <Typography.Title className="title" level={3}>
          Your request for membership into the UPSL has been successfully submitted!
        </Typography.Title>
        <Typography.Text className="description">
          Someone from our leadership team will contact you in the coming days to discuss your request in more detail,
          answer any questions and detail the application process and next steps.
          <br />
          <br />
          Thank you for your interest in the UPSL!
        </Typography.Text>
        <Button href="https://premier.upsl.com/" icon={<IconArrowLeft />} size="large">
          Back to UPSL web-site
        </Button>
      </div>
    </Col>
  );
};

const ErrorMessage = ({ onExit }: { onExit: () => void }) => {
  return (
    <Col flex="1" className="team-registration__message team-registration__message-error">
      <div>
        <IconError className="icon" />
        <Typography.Title className="title" level={3}>
          Something went wrong
        </Typography.Title>
        <Typography.Text className="description">
          We couldn’t submit your application due to a technical issue. Please try again in a few minutes. If the
          problem persists, contact us.
        </Typography.Text>
        <Button onClick={onExit} icon={<IconArrowLeft />} size="large">
          Try Again
        </Button>
      </div>
    </Col>
  );
};
